import { WorkflowStage } from "../../constants/workflowStages"
/**
 * Workflow action buttons for the DTA's current stage.
 */

import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import React, { useState } from "react"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import { useNotifications } from "../../contexts/NotificationContext"
import {
  useActivateDta,
  useApproveDta,
  useDeactivateDta,
  usePublishDta,
  useReactivateDta,
} from "../../hooks/queries/useWorkflow"
import MissingFieldsList, { type ValidationError } from "../shared/MissingFieldsList"

/**
 * Confirmation modal and success toast for each major transition, keyed by the stage it fires from.
 */

const CONFIRM_CONFIG: Record<
  string,
  {
    verb: string
    title: string
    body: string
    success: { subject: string; message: string }
  }
> = {
  DRAFT: {
    verb: "Submit for Review",
    title: "Submit this Data Transfer Agreement for vendor review?",
    body: "This notifies the vendor users with access and moves the Data Transfer Agreement to the REVIEW stage.",
    success: {
      subject: "Submitted for review",
      message:
        "The Data Transfer Agreement moved to REVIEW and the vendor users with access were notified.",
    },
  },
  REVIEW: {
    verb: "Approve",
    title: "Approve this Data Transfer Agreement?",
    body: "This records your approval for the current review stage.",
    success: {
      subject: "Approval recorded",
      message: "Your approval was recorded for this Data Transfer Agreement.",
    },
  },
  APPROVED: {
    verb: "Activate",
    title: "Activate this Data Transfer Agreement?",
    body: "This activates the Data Transfer Agreement so production data transfers can begin.",
    success: {
      subject: "Data Transfer Agreement activated",
      message: "Production data transfers can now begin.",
    },
  },
  ACTIVE: {
    verb: "Deactivate",
    title: "Deactivate this Data Transfer Agreement?",
    body: "This stops production data transfers. The Data Transfer Agreement can be reactivated later.",
    success: {
      subject: "Data Transfer Agreement deactivated",
      message: "Production data transfers have been stopped. You can reactivate it later.",
    },
  },
  INACTIVE: {
    verb: "Reactivate",
    title: "Reactivate this Data Transfer Agreement?",
    body: "This resumes production data transfers for this Data Transfer Agreement.",
    success: {
      subject: "Data Transfer Agreement reactivated",
      message: "Production data transfers have resumed.",
    },
  },
}

interface WorkflowActionsProps {
  dtaId: string
  studyId: string
  /**
   * DTA's vendor, used to invalidate the vendor-scoped DTA list after a transition (vendor
   * visibility is stage-gated).
   */
  vendorId?: string
  currentStage: string
  currentVersion: string
  canTransition: boolean
  transitionOptions: string[]
  nextActions?: Array<{ action: string; description: string; required_role?: string }>
  userApproved?: boolean
  onActionComplete?: () => void | Promise<void>
  onSubmitData?: () => void
  /**
   * Report a failed transition to the parent, since the header action row has no room for an inline
   * Alert. The message may be a node, e.g. a list of missing fields.
   */
  onError?: (message: React.ReactNode) => void
  /**
   * Called before publish. Return errors to block, empty array to proceed.
   */
  validateBeforePublish?: () => Array<{ field: string; tab: string; message: string }>
}

const WorkflowActions: React.FC<WorkflowActionsProps> = ({
  dtaId,
  studyId,
  vendorId,
  currentVersion,
  currentStage,
  canTransition,
  transitionOptions,
  nextActions: _nextActions = [],
  userApproved = false,
  onActionComplete,
  onSubmitData,
  onError,
  validateBeforePublish,
}) => {
  const { showToast } = useNotifications()
  const [loading, setLoading] = useState<string | null>(null)

  const scope = { studyId, vendorId }
  const publishDta = usePublishDta(dtaId, scope)
  const approveDta = useApproveDta(dtaId, scope)
  const activateDta = useActivateDta(dtaId, scope)
  const deactivateDta = useDeactivateDta(dtaId, scope)
  const reactivateDta = useReactivateDta(dtaId, scope)

  // Stage whose action awaits confirmation; null when the modal is closed.
  const [confirmStage, setConfirmStage] = useState<string | null>(null)

  const { experience } = useMyAccess()
  const userRole = experience === "internal" ? "DAE" : experience === "external" ? "VENDOR" : null

  const parseApiError = (err: unknown, fallback: string): React.ReactNode => {
    const message = err instanceof Error ? err.message : fallback
    const jsonMatch = message.match(/- ({.+})$/)
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[1])
        // DRAFT -> REVIEW returns {"error", "missing_fields": [{"field", "tab", "message"}]} when
        // the DTA is not ready. Show one bullet per missing field.
        if (Array.isArray(parsed.missing_fields) && parsed.missing_fields.length > 0) {
          const fields = parsed.missing_fields as ValidationError[]
          return (
            <>
              {parsed.error ?? "DTA is missing required fields"}
              <MissingFieldsList errors={fields} />
            </>
          )
        }
        return parsed.error || message
      } catch {
        return message
      }
    }
    return message
  }

  const sendApproval = async (stage: string) => {
    setConfirmStage(null)
    try {
      setLoading("approve")
      onError?.("")
      if (stage === WorkflowStage.Draft) {
        // Validate required fields before submitting for review.
        if (validateBeforePublish) {
          const errors = validateBeforePublish()
          if (errors.length > 0) {
            setLoading(null)
            return
          }
        }
        await publishDta.mutateAsync({ version: currentVersion })
      } else if (stage === WorkflowStage.Review) {
        await approveDta.mutateAsync({})
        // Wait briefly for the async auto-transition that follows approval.
        await new Promise((resolve) => setTimeout(resolve, 1500))
      } else if (stage === WorkflowStage.Approved) {
        await activateDta.mutateAsync({ version: currentVersion })
      } else if (stage === WorkflowStage.Active) {
        await deactivateDta.mutateAsync({ version: currentVersion })
      } else if (stage === WorkflowStage.Inactive) {
        await reactivateDta.mutateAsync({ version: currentVersion })
      }
      // Toast on success; the transition gives no other feedback.
      const success = CONFIRM_CONFIG[stage]?.success
      if (success) {
        showToast({ subject: success.subject, message: success.message, variant: "success" })
      }
      if (onActionComplete) await onActionComplete()
    } catch (err) {
      onError?.(parseApiError(err, "Failed to complete action"))
    } finally {
      setLoading(null)
    }
  }

  // Disable buttons while a call is in flight or the version is read-only.
  const actionsBusy = loading !== null || !canTransition

  const getActionButton = () => {
    if (transitionOptions.length === 0) return null

    switch (currentStage) {
      case WorkflowStage.Draft:
        if (userRole !== "DAE") {
          return <StatusIndicator type="pending">DAE is drafting</StatusIndicator>
        }
        return (
          <Button
            variant="primary"
            iconName="angle-right"
            iconAlign="right"
            onClick={() => setConfirmStage(currentStage)}
            loading={loading === "approve"}
            disabled={actionsBusy}
          >
            Submit for Review
          </Button>
        )

      case WorkflowStage.Review:
        if (userRole === null) {
          return null
        }
        if (userApproved) {
          return <StatusIndicator type="success">You have approved</StatusIndicator>
        }
        return (
          <Button
            variant="primary"
            iconName="check"
            onClick={() => setConfirmStage(currentStage)}
            loading={loading === "approve"}
            disabled={actionsBusy}
          >
            Approve
          </Button>
        )

      case WorkflowStage.Approved:
        if (userRole !== "DAE") {
          return <StatusIndicator type="pending">Waiting for DAE to activate</StatusIndicator>
        }
        return (
          <Button
            variant="primary"
            onClick={() => setConfirmStage(currentStage)}
            loading={loading === "approve"}
            disabled={actionsBusy}
          >
            Activate DTA
          </Button>
        )

      case WorkflowStage.Active:
        if (userRole !== "DAE") {
          return null
        }
        return (
          <Button
            variant="normal"
            iconName="status-negative"
            onClick={() => setConfirmStage(currentStage)}
            loading={loading === "approve"}
            disabled={actionsBusy}
          >
            Deactivate
          </Button>
        )

      case WorkflowStage.Inactive:
        if (userRole !== "DAE") {
          return null
        }
        return (
          <Button
            variant="primary"
            onClick={() => setConfirmStage(currentStage)}
            loading={loading === "approve"}
            disabled={actionsBusy}
          >
            Reactivate
          </Button>
        )

      case "INVALID":
        return <StatusIndicator type="warning">Version Invalid</StatusIndicator>

      default:
        return null
    }
  }

  const confirmConfig = confirmStage ? CONFIRM_CONFIG[confirmStage] : null

  return (
    <>
      <SpaceBetween size="s" direction="horizontal">
        {userRole === "VENDOR" && currentStage === WorkflowStage.Review && !userApproved && (
          <Button
            variant="normal"
            disabled
            disabledReason="Coming soon"
            ariaLabel="Request Changes is coming soon"
          >
            Request Changes
          </Button>
        )}
        {onSubmitData && (
          <Button variant="normal" iconName="upload" onClick={onSubmitData} disabled={actionsBusy}>
            Submit Data
          </Button>
        )}
        {getActionButton()}
      </SpaceBetween>

      {confirmStage && confirmConfig && (
        <Modal
          visible
          onDismiss={() => setConfirmStage(null)}
          header={confirmConfig.title}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setConfirmStage(null)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => void sendApproval(confirmStage)}>
                  {confirmConfig.verb}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {confirmConfig.body}
        </Modal>
      )}
    </>
  )
}

export default WorkflowActions
